import React, { FC, memo, useEffect, useMemo, useRef } from 'react';
import { DisciplineRouteType } from '@om-fe/core/components/discipline-anchor/discipline-route.utils';
import { isRpcStateReady, RpcState, RpcStateReady, RpcStateStatus, useRpcState } from '@om-fe/widgets/dist/common/rpc/rpc-state.hooks'
import { LocationModel } from '@om-fe/models';
import { TournamentBreadcrumbs } from './tournament-breadcrumbs';
import { breadcrumbsBem, SCROLL_INTO_VIEW_OPTIONS } from './breadcrumbs.constants';
import { GetBreadcrumbsResponse } from '../../common/breadcrumbsResponse';
import DisciplineAnchor from '@om-fe/core/components/discipline-anchor/discipline-anchor';
import TournamentAnchor from '@om-fe/core/components/tournament-anchor/tournament-anchor';
import getConfig, { OMFE2ConfigModel } from '@om-fe/core/utils/config';

interface LocationBreadcrumbsPublicProps {
    readonly isLarge: boolean;
    readonly disciplineId: string;
    readonly locationId: string;
    readonly tournamentId?: string;
    readonly routeType: DisciplineRouteType;
    readonly disciplineName: string;
    readonly lang: string;
    readonly eventId?: string;
    readonly isLocationType: boolean;
    readonly isCategoryType: boolean;
    readonly parentId?: string;
    readonly isLive?: boolean;
}

export type LocationBreadcrumbsProps = LocationBreadcrumbsPublicProps;

export const LocationBreadcrumbs: FC<LocationBreadcrumbsProps> = memo(function LocationBreadcrumbsComponent(
    {
        lang,
        isLarge,
        disciplineId,
        locationId,
        tournamentId,
        disciplineName,
        eventId,
        isLocationType,
        isCategoryType,
        parentId,
        routeType,
        isLive,
    }: LocationBreadcrumbsProps
) {
    const params = useMemo(() => {
        return { sportId: disciplineId, lang, venueId: locationId };
    }, [lang, disciplineId , locationId]);
    const [response] = useRpcState<GetBreadcrumbsResponse<LocationModel>>('locations', params);
    const ref: React.RefObject<HTMLSpanElement> = useRef<HTMLSpanElement>();
    const config: OMFE2ConfigModel = getConfig();
    const { status }: RpcState<GetBreadcrumbsResponse<LocationModel>> = response;

    useEffect(() => {
        if (status === RpcStateStatus.Success && ref.current && !tournamentId) {
            if (!config.virtualState) {
                ref.current.scrollIntoView(SCROLL_INTO_VIEW_OPTIONS);
            }
        }
    }, [status, tournamentId]);

    if (!isRpcStateReady(response)) {
        return null;
    }

    const { data }: RpcStateReady<GetBreadcrumbsResponse<LocationModel>> = response;

    const locationName: string | undefined = data.records[0]?.name || locationId;

    if (tournamentId) {
        return (
            <>
                {!parentId && (
                    <>
                        {isLive ? (
                            <DisciplineAnchor
                                className={breadcrumbsBem('CurrentItem', 'Location')}
                                title={locationName}
                                disciplineId={disciplineId}
                                disciplineName={disciplineName}
                                childId={locationId}
                                childName={locationName}
                                disciplineRouteType={routeType}
                                isLive={isLive}
                            >
                                <span className={breadcrumbsBem('Text', 'Location')}>{locationName}</span>
                            </DisciplineAnchor>
                        ) : (
                            <TournamentAnchor
                                title={'all'}
                                className={breadcrumbsBem('CurrentItem', 'Location')}
                                disciplineId={disciplineId}
                                disciplineName={disciplineName}
                                locationId={locationId}
                                locationName={locationName}
                                tournamentId={0}
                                tournamentName={'all'}
                            >
                                <span className={breadcrumbsBem('Text', 'Location')}>{locationName}</span>
                            </TournamentAnchor>
                        )}
                        <span className={breadcrumbsBem({
                            element: 'Separator',
                            modifiers: 'Tournament',
                        })}>&#47;
                        </span>
                    </>
                )}
                <TournamentBreadcrumbs
                    tournamentId={tournamentId}
                    isLarge={isLarge}
                    disciplineId={disciplineId}
                    locationId={locationId}
                    disciplineName={disciplineName}
                    locationName={locationName}
                    isLocationType={isLocationType}
                    isCategoryType={isCategoryType}
                    lang={lang}
                    eventId={eventId}
                />
            </>
        );
    }

    return (
        <span className={breadcrumbsBem(isLarge ? 'Text--Location' : 'CurrentItem--Location')} ref={ref}>{locationName}</span>
    );
});
